import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Trash2, FileText, Receipt, CreditCard,
  MapPin, Mail, Phone, Building2, Calendar, ExternalLink,
} from "lucide-react";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function CustomerProfile() {
  const nav = useNavigate();
  const { id } = useParams();
  const [cust, setCust] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("history");

  useEffect(() => {
    let live = true;
    Promise.all([
      api.get(`/customers/${id}`),
      api.get(`/customers/${id}/history`),
    ]).then(([{ data: c }, { data: h }]) => {
      if (!live) return;
      setCust(c); setHistory(h || []);
    }).catch(() => toast.error("Could not load customer"))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [id]);

  const totals = useMemo(() => {
    const business = history.filter((h) => h.type === "invoice").reduce((s, x) => s + (x.amount || 0), 0);
    return {
      quotations: history.filter((h) => h.type === "quotation").length,
      invoices: history.filter((h) => h.type === "invoice").length,
      vouchers: history.filter((h) => h.type === "voucher").length,
      business,
    };
  }, [history]);

  const timeline = useMemo(() => {
    // Group by YYYY-MM-DD (descending)
    const groups = new Map();
    history.forEach((h) => {
      const d = (h.date || "").slice(0, 10) || "0000-00-00";
      if (!groups.has(d)) groups.set(d, []);
      groups.get(d).push(h);
    });
    return Array.from(groups.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [history]);

  if (loading) return <Frame><div className="p-10 text-slate-400">Loading…</div></Frame>;
  if (!cust) return null;

  const nm = cust.customer_type === "corporate" && cust.company_name
    ? cust.company_name
    : [cust.title, cust.first_name, cust.middle_name, cust.last_name].filter(Boolean).join(" ") || cust.email || "Unnamed";

  const del = async () => {
    if (!window.confirm("Delete this customer? Their history references are kept but no longer linked.")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      nav("/customers");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Frame>
      <header className="bg-white border-b border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 flex items-baseline justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/customers")} className="text-slate-500 hover:text-slate-900" data-testid="back-btn">
              <ArrowLeft size={16} />
            </button>
            <div>
              <div className="overline text-[10px] tracking-widest text-slate-500">
                {cust.customer_id} · {cust.customer_type}
              </div>
              <h1 data-testid="cust-name" className="font-editorial text-3xl mt-1">{nm}</h1>
              <div className="text-xs text-slate-500 mt-1">
                Created {cust.created_at ? new Date(cust.created_at).toLocaleDateString() : "—"}
                {cust.last_activity_at && <> · Last activity {new Date(cust.last_activity_at).toLocaleDateString()}</>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/customers/${id}/edit`} data-testid="edit-btn"
                  className="text-xs px-3 py-2 border border-slate-300 rounded hover:bg-slate-50 inline-flex items-center gap-1.5">
              <Pencil size={12} /> Edit
            </Link>
            <button onClick={del} data-testid="delete-btn"
                    className="text-xs px-3 py-2 border border-red-200 text-red-600 rounded hover:bg-red-50 inline-flex items-center gap-1.5">
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        {/* Info grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card title="Contact">
            <InfoRow icon={Phone} label="Mobile" value={cust.mobile} />
            <InfoRow icon={Phone} label="Alt. Mobile" value={cust.alt_mobile} />
            <InfoRow icon={Phone} label="WhatsApp" value={cust.whatsapp} />
            <InfoRow icon={Mail} label="Email" value={cust.email} />
            <InfoRow icon={Mail} label="Alt. Email" value={cust.alt_email} />
          </Card>
          <Card title="Address">
            <InfoRow icon={MapPin} label="Address" value={cust.address} />
            <InfoRow icon={MapPin} label="City" value={cust.city} />
            <InfoRow icon={MapPin} label="State" value={cust.state} />
            <InfoRow icon={MapPin} label="Country" value={cust.country} />
            <InfoRow icon={MapPin} label="PIN" value={cust.pincode} />
          </Card>
          {cust.customer_type === "corporate" ? (
            <Card title="Corporate">
              <InfoRow icon={Building2} label="Company" value={cust.company_name} />
              <InfoRow label="GSTIN" value={cust.company_gstin} />
              <InfoRow label="PAN" value={cust.company_pan} />
              <InfoRow label="Billing Contact" value={cust.billing_contact} />
              <InfoRow label="Billing Email" value={cust.billing_email} />
              <InfoRow label="Billing Mobile" value={cust.billing_mobile} />
            </Card>
          ) : (
            <Card title="Personal">
              <InfoRow label="DOB" value={cust.date_of_birth} />
              <InfoRow label="Gender" value={cust.gender} />
              <InfoRow label="Nationality" value={cust.nationality} />
              <InfoRow label="Passport" value={cust.passport_number} />
              <InfoRow label="PAN" value={cust.pan_number} />
            </Card>
          )}
        </div>

        {/* Prefs + summary counts */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card title="Preferences" span={2}>
            <div className="text-xs text-slate-600 space-y-1">
              {cust.preferred_destinations?.length > 0 && <div>Destinations: <b>{cust.preferred_destinations.join(", ")}</b></div>}
              {cust.preferred_hotel_category && <div>Hotel: <b>{cust.preferred_hotel_category}</b></div>}
              {cust.preferred_room_category && <div>Room: <b>{cust.preferred_room_category}</b></div>}
              {cust.preferred_meal_plan && <div>Meal: <b>{cust.preferred_meal_plan}</b></div>}
              {cust.preferred_airline && <div>Airline: <b>{cust.preferred_airline}</b></div>}
              {cust.seat_preference && <div>Seat: <b>{cust.seat_preference}</b></div>}
              {cust.special_requirements && <div className="mt-2 italic text-slate-500">"{cust.special_requirements}"</div>}
            </div>
          </Card>
          <TotalCard icon={FileText} label="Quotations" value={totals.quotations} />
          <TotalCard icon={Receipt} label="Invoices" value={totals.invoices} sub={rupees(totals.business)} />
        </div>

        {/* Tabs */}
        <div className="bg-white border border-border rounded overflow-hidden">
          <div className="border-b border-border flex">
            {[["history", "History"], ["timeline", "Timeline"]].map(([k, l]) => (
              <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${tab === k
                        ? "border-emerald-600 text-emerald-700"
                        : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                {l} <span className="ml-1 text-[10px] text-slate-400">({history.length})</span>
              </button>
            ))}
          </div>
          {tab === "history" && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3">Date</th>
                  <th className="text-left px-6 py-3">Reference</th>
                  <th className="text-left px-6 py-3">Type</th>
                  <th className="text-left px-6 py-3">Details</th>
                  <th className="text-right px-6 py-3">Amount</th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr><td colSpan={7} className="px-6 py-10 text-center text-slate-400">
                    No history yet. Quotations, invoices and vouchers linked to this customer will show up here.
                  </td></tr>
                )}
                {history.map((h) => (
                  <tr key={`${h.type}-${h.id}`} className="border-t border-border hover:bg-slate-50">
                    <td className="px-6 py-3 text-xs text-slate-600">{h.date ? new Date(h.date).toLocaleDateString() : "—"}</td>
                    <td className="px-6 py-3 font-mono-e text-xs">{h.reference}</td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${TYPE_COLORS[h.type]}`}>
                        <TypeIcon type={h.type} /> {h.type}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm text-slate-800">
                      {h.title}
                      {h.destination && <span className="text-slate-400"> · {h.destination}</span>}
                    </td>
                    <td className="px-6 py-3 text-right text-sm">{h.amount ? rupees(h.amount) : "—"}</td>
                    <td className="px-6 py-3 text-xs uppercase text-slate-500">{h.status}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={h.route} data-testid={`open-${h.type}-${h.id}`} className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 text-xs">
                        View <ExternalLink size={11} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {tab === "timeline" && (
            <div className="p-6">
              {timeline.length === 0 && <div className="text-slate-400 text-center py-8">No activity yet</div>}
              <div className="space-y-6">
                {timeline.map(([day, items]) => (
                  <div key={day} className="flex gap-4">
                    <div className="w-28 shrink-0 pt-1">
                      <div className="text-xs uppercase tracking-widest text-slate-500">
                        <Calendar size={12} className="inline mr-1" />
                        {day === "0000-00-00" ? "—" : new Date(day + "T00:00:00Z").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" })}
                      </div>
                    </div>
                    <div className="flex-1 border-l-2 border-emerald-200 pl-5 space-y-3 relative">
                      {items.map((it) => (
                        <div key={`${it.type}-${it.id}`} className="relative">
                          <div className="absolute -left-[26px] top-1.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
                          <Link to={it.route} className="block bg-slate-50 hover:bg-emerald-50 border border-slate-200 rounded px-4 py-3">
                            <div className="text-xs uppercase tracking-widest text-emerald-700">{it.type} · {it.reference}</div>
                            <div className="text-sm text-slate-900 mt-1">
                              {it.title}{it.destination && <span className="text-slate-500"> — {it.destination}</span>}
                            </div>
                            {it.amount > 0 && <div className="text-xs text-slate-500 mt-0.5">{rupees(it.amount)} · {it.status}</div>}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Frame>
  );
}

const TYPE_COLORS = {
  quotation: "bg-emerald-50 text-emerald-700 border-emerald-200",
  invoice: "bg-amber-50 text-amber-700 border-amber-200",
  voucher: "bg-blue-50 text-blue-700 border-blue-200",
};
function TypeIcon({ type }) {
  const I = type === "invoice" ? Receipt : type === "voucher" ? CreditCard : FileText;
  return <I size={10} />;
}

function Frame({ children }) {
  return <div className="min-h-screen flex bg-slate-50"><Sidebar /><main className="flex-1">{children}</main></div>;
}
function Card({ title, span = 1, children }) {
  return (
    <section className={`bg-white border border-border rounded p-5 md:col-span-${span}`}>
      <h2 className="text-xs font-semibold text-emerald-700 mb-3 uppercase tracking-widest">{title}</h2>
      {children}
    </section>
  );
}
function TotalCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-border rounded p-4">
      <div className="w-8 h-8 rounded flex items-center justify-center mb-3 bg-emerald-50 text-emerald-600"><Icon size={16} /></div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 font-editorial text-2xl">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-xs text-slate-700 mb-1.5">
      {Icon && <Icon size={11} className="text-slate-400 mt-0.5 shrink-0" />}
      <span className="text-slate-500 w-24 shrink-0">{label}</span>
      <span className="flex-1 min-w-0 break-words">{value}</span>
    </div>
  );
}
